import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  InputSignal,
  InputSignalWithTransform,
  ModelSignal,
  OutputEmitterRef,
  Signal,
  WritableSignal,
  computed,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';

import { EntityIcons } from '../entity-icons';
import { EntityType } from '../entity-type';

import { EntityItemActionButtonComponent } from './entity-item-action-button/entity-item-action-button.component';

@Component({
  selector: 'mp-entity-item',
  templateUrl: './entity-item.component.html',
  styleUrl: './entity-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.item--removed]': 'removed()',
    '[class.item--hidden]': 'hidden()',
  },
  imports: [MatChipsModule, MatIconModule, EntityItemActionButtonComponent],
})
export class EntityItemComponent {
  readonly hidden: ModelSignal<boolean> = model<boolean>(false);

  readonly chipsLabels: InputSignal<string[]> = input<string[]>([]);

  readonly id: InputSignal<string | undefined> = input<string>();
  readonly label: InputSignal<string | undefined> = input<string>();
  readonly meta: InputSignal<string | undefined> = input<string>();

  readonly icon: InputSignal<string | undefined> = input<string>();

  /**
   * Convenience property that sets the components {@link itemIcon} based on a use-case.
   *
   * All this does is basically lookup the required material icon from the {@link EntityIcons} map.
   * NOTE: Only works if the {@link icon} input is not set as it takes precedence.
   */
  readonly type: InputSignal<EntityType | undefined> = input<EntityType>();

  readonly editMode: InputSignalWithTransform<boolean, BooleanInput> = input<boolean, BooleanInput>(false, {
    transform: coerceBooleanProperty,
  });

  readonly remove: OutputEmitterRef<string | undefined> = output<string | undefined>();
  readonly undo: OutputEmitterRef<string | undefined> = output<string | undefined>();

  protected readonly removed: WritableSignal<boolean> = signal<boolean>(false);

  protected readonly itemIcon: Signal<string | undefined> = computed(() => {
    const entityType: EntityType | undefined = this.type();
    if (!this.icon() && entityType) {
      return EntityIcons[entityType];
    }
    return this.icon();
  });

  removeClicked() {
    this.removed.set(true);
    this.remove.emit(this.id());
  }

  undoClicked() {
    this.removed.set(false);
    this.undo.emit(this.id());
  }
}
